import { IBasePickerSuggestionsProps, ValidationState } from "@fluentui/react";
import { InteractiveElementProps } from "../../shared/props";

export interface PeoplePickerProps extends InteractiveElementProps {
    /**
     * A callback for what should happen when a person types text into the input.
     * Provides the already selected items so the resolver can filter them out.
     */
    readonly onResolveSuggestions: (filter: string, selectedItems?: readonly PeoplePickerPersona[]) => readonly PeoplePickerPersona[] | Promise<readonly PeoplePickerPersona[]>;

    /** Callback for when the list of selected items changes */
    readonly onChange: (items?: readonly PeoplePickerPersona[]) => void;

    /** aria-label for the remove button on selected items */
    readonly removeButtonAriaLabel: string;

    /** Props to pass to the suggestions popout */
    readonly pickerSuggestionsProps: PickerSuggestionsProps;

    /** Initial selected items to show within the picker */
    readonly defaultSelectedItems?: readonly PeoplePickerPersona[];

    /**
     * A callback for what should happen when suggestions are shown without input provided.
     * Provides the already selected items so the resolver can filter them out.
     */
    readonly onEmptyResolveSuggestions?: (selectedItems?: readonly PeoplePickerPersona[]) => readonly PeoplePickerPersona[];

    /**
     * Function to validate raw user text input into a selected item when the user presses enter/return while typing.
     * This function must be provided if you want users to be able to select manually typed values in addition to ones
     * from the suggestions.
     */
    readonly onValidateInput?: (input: string) => ValidState;
}

export interface PeoplePickerPersona {
    /** Optional profile image URL. Fallback image is the persona initials from Fluent using the `text` value */
    readonly imageUrl?: string;

    /** Text to show for the persona */
    readonly text: string;

    /** Secondary text to display underneath the `text` */
    readonly secondaryText: string;
}

interface PickerSuggestionsProps extends Pick<IBasePickerSuggestionsProps<PeoplePickerPersona>,
    "suggestionsHeaderText"
    | "mostRecentlyUsedHeaderText"
    | "noResultsFoundText"
    | "loadingText"
> {
    /** aria-label for the suggestions container */
    readonly suggestionsContainerAriaLabel: string;
}

// Object to map string literals to Fluent's enum so we don't ship enums
export const validationMap = {
    /** Accepts the item */
    valid: ValidationState.valid,
    /** Shows the item with a "!" in the persona icon and gives the item's text error styling */
    warning: ValidationState.warning,
    /** Stops the item from being created */
    invalid: ValidationState.invalid
} as const;

type ValidState = keyof typeof validationMap;
